import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Wrench } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

export function AuthForm({ mode }) {
  const { t } = useI18n();
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const isLogin = mode === "login";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (isLogin) await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      toast.success(isLogin ? t("auth.login.title") : t("auth.register.title"));
      navigate(location.state?.from || "/garage");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-4 py-12">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
        className="rounded-2xl border border-border bg-white p-8 shadow-sm">
        <div className="mb-6 flex items-center gap-2">
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-primary text-white"><Wrench className="h-5 w-5" /></span>
          <span className="font-heading text-lg font-bold tracking-tighter text-slate-900">PartFinder<span className="text-primary">.sk</span></span>
        </div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">{isLogin ? t("auth.login.title") : t("auth.register.title")}</h1>
        <p className="mt-1 text-sm text-slate-500">{isLogin ? t("auth.login.subtitle") : t("auth.register.subtitle")}</p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          {!isLogin && (
            <div>
              <label className="text-sm font-medium text-slate-700">{t("auth.name")}</label>
              <input data-testid="auth-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1.5 w-full rounded-lg border border-border px-4 py-2.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-primary" />
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-slate-700">{t("auth.email")}</label>
            <input data-testid="auth-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1.5 w-full rounded-lg border border-border px-4 py-2.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-primary" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">{t("auth.password")}</label>
            <input data-testid="auth-password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="mt-1.5 w-full rounded-lg border border-border px-4 py-2.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-primary" />
          </div>
          {error && <p data-testid="auth-error" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}
          <button type="submit" data-testid="auth-submit" disabled={busy}
            className="w-full rounded-full bg-primary px-6 py-3 font-heading font-semibold text-white transition-colors duration-200 hover:bg-blue-600 disabled:opacity-60">
            {busy ? t("common.loading") : isLogin ? t("auth.login.button") : t("auth.register.button")}
          </button>
        </form>
        <div className="mt-5 text-center text-sm text-slate-500">
          <Link data-testid="auth-switch" to={isLogin ? "/register" : "/login"} className="font-medium text-primary hover:underline">
            {isLogin ? t("auth.toRegister") : t("auth.toLogin")}
          </Link>
        </div>
      </motion.div>
    </div>
  );
}

export function Login() { return <AuthForm mode="login" />; }
export function Register() { return <AuthForm mode="register" />; }
